import { SolutionScenario } from '@/types/scenarios/solution';
import { Hammer, Phone, Calendar, Users, Wrench, Zap, Droplets, Flame, ThermometerSun, ClipboardList } from 'lucide-react';

export const trades: SolutionScenario = {
  meta: {
    id: 'trades',
    slug: 'trades',
    name: 'Trades',
    tagline: 'Genies that handle the phones so you can stay on the tools',
    description: "A genie that triages emergencies, books service calls, answers the same homeowner questions every day, and runs the phones so your team can stay on the tools.",
    market: 'Trades',
    subMarkets: ['Plumbing', 'Electrical', 'HVAC', 'Roofing', 'General Contractors', 'Landscaping'],
    specificNeeds: [
      '24/7 emergency call handling',
      'Appointment scheduling',
      'Quote request capture',
      'Service area verification',
      'Job status updates',
      'After hours lead capture'
    ],
    commonRoles: ['Business Owners', 'Office Managers', 'Dispatchers', 'Service Technicians'],
    marketSize: '$1.8 trillion in the US',
    isFeatured: true
  },

  hero: {
    headline: 'Phones answered, jobs booked',
    headlineGradient: 'Phones answered,',
    headlineRest: 'jobs booked',
    subheadline: "A genie that triages emergencies, books service calls, answers the same homeowner questions every day, and runs the phones so your team can stay on the tools.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'interactive-faq',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'emergency-dispatch',
      name: 'Emergency Call Handling',
      description: 'Capture and triage emergency service calls 24/7',
      department: 'Service',
      icon: Zap,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        '24/7 emergency response',
        'Priority call routing',
        'Capture urgent job details',
        'Never miss an emergency call'
      ]
    },
    {
      id: 'appointment-booking',
      name: 'Appointment Scheduling',
      description: 'Let customers book service appointments around the clock',
      department: 'Operations',
      icon: Calendar,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Self service booking 24/7',
        'Automatic calendar sync',
        'Confirmation and reminders',
        'Reduce no-shows by 40%'
      ]
    },
    {
      id: 'quote-requests',
      name: 'Quote Request Capture',
      description: 'Capture project details and quote requests from potential customers',
      department: 'Sales',
      icon: ClipboardList,
      playbookIds: ['lead-capture'],
      benefits: [
        'Capture leads after hours',
        'Gather project scope upfront',
        'Service area verification',
        'Qualify jobs before callbacks'
      ]
    },
    {
      id: 'plumbing-support',
      name: 'Plumbing Services',
      description: 'Handle plumbing emergencies, drain cleaning, and water heater inquiries',
      department: 'Service',
      icon: Droplets,
      playbookIds: ['lead-capture', 'appointment-scheduler'],
      benefits: [
        'Emergency leak response',
        'Drain cleaning scheduling',
        'Water heater service calls',
        'Capture bathroom/kitchen remodel leads'
      ]
    },
    {
      id: 'hvac-support',
      name: 'HVAC Services',
      description: 'Manage heating and cooling emergencies, maintenance, and installations',
      department: 'Service',
      icon: ThermometerSun,
      playbookIds: ['lead-capture', 'appointment-scheduler'],
      benefits: [
        'No-heat/no-cool emergency handling',
        'Seasonal maintenance scheduling',
        'System replacement quotes',
        'Filter change reminders'
      ]
    },
    {
      id: 'electrical-support',
      name: 'Electrical Services',
      description: 'Handle electrical emergencies, panel upgrades, and installation requests',
      department: 'Service',
      icon: Zap,
      playbookIds: ['lead-capture', 'appointment-scheduler'],
      benefits: [
        'Power outage response',
        'Panel upgrade inquiries',
        'EV charger installation leads',
        'Safety inspection scheduling'
      ]
    }
  ],

  challenges: [
    {
      title: 'Missing Calls While Working',
      description: 'Trade professionals can\'t answer phones while on job sites, leading to lost business',
      impact: 'high',
      affectedAreas: ['Revenue', 'Lead capture', 'Customer acquisition']
    },
    {
      title: 'After Hours Emergencies',
      description: 'Plumbing leaks, HVAC failures, and electrical issues don\'t wait for business hours',
      impact: 'high',
      affectedAreas: ['Customer service', 'Emergency revenue', 'Reputation']
    },
    {
      title: 'Scheduling Chaos',
      description: 'Managing appointments across multiple technicians and locations is complex',
      impact: 'medium',
      affectedAreas: ['Efficiency', 'Customer satisfaction', 'Technician utilization']
    },
    {
      title: 'Inconsistent Customer Experience',
      description: 'Call handling varies when different people answer, affecting professionalism',
      impact: 'medium',
      affectedAreas: ['Brand perception', 'Customer trust', 'Review ratings']
    }
  ],

  solutions: [
    {
      challenge: 'Missing calls on job sites',
      solution: 'Deploy Voice Genie to answer every call professionally',
      implementation: 'Forward business line to Help Genie when unavailable',
      expectedResults: [
        'Capture 100% of incoming leads',
        'Professional response every time',
        'Detailed message with job information'
      ]
    },
    {
      challenge: 'After hours emergency coverage',
      solution: '24/7 emergency call triage and dispatch',
      implementation: 'Configure emergency keywords and escalation procedures',
      expectedResults: [
        'Never miss an emergency call',
        'Proper severity assessment',
        'Premium emergency service revenue'
      ]
    },
    {
      challenge: 'Appointment scheduling burden',
      solution: 'Self service appointment booking with calendar integration',
      implementation: 'Connect to business calendar and configure availability',
      expectedResults: [
        '60% reduction in scheduling calls',
        'Fewer no-shows with reminders',
        'Better technician utilization'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'ServiceTitan',
        category: 'Field Service',
        description: 'Leading software for home service businesses',
        required: false,
        popularityScore: 95
      },
      {
        name: 'Housecall Pro',
        category: 'Field Service',
        description: 'All in one business solution for home services',
        required: false,
        popularityScore: 90
      },
      {
        name: 'Jobber',
        category: 'Field Service',
        description: 'Scheduling and invoicing for service businesses',
        required: false,
        popularityScore: 88
      },
      {
        name: 'FieldEdge',
        category: 'Field Service',
        description: 'Service management for contractors',
        required: false,
        popularityScore: 82
      }
    ],
    common: [
      {
        name: 'Google Calendar',
        category: 'Scheduling',
        description: 'Calendar integration for appointments',
        required: false,
        popularityScore: 95
      },
      {
        name: 'QuickBooks',
        category: 'Accounting',
        description: 'Business accounting software',
        required: false,
        popularityScore: 92
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1 week',
    typicalROI: '520%',
    adoptionRate: '94% customer satisfaction',
    customerSatisfaction: '4.9/5'
  },

  demoConfig: {
    firstMessage: "Hi! Thanks for calling. I can help you schedule a service appointment, request a quote, or if you have an emergency, I'll make sure we get someone to help you right away. What do you need help with today?",
    systemPrompt: "You are a friendly and professional assistant for a home services company. Help customers with scheduling appointments, capturing quote requests, and triaging emergencies. Always verify the service area and capture complete contact information. For emergencies, quickly assess severity and reassure the customer that help is on the way.",
    marketContext: "Home services trade business handling residential and commercial service calls",
    exampleScenarios: [
      "I have a water leak under my sink",
      "My AC stopped working and it's 95 degrees",
      "I need a quote for rewiring my garage",
      "Can I schedule a furnace tune-up?"
    ]
  },

  caseStudies: [
    {
      companyName: 'Premier Plumbing & HVAC',
      companySize: '15 technicians',
      location: 'Texas',
      challenge: 'Owner and office manager couldn\'t keep up with call volume, missing 30% of incoming calls during busy season',
      implementation: 'Deployed Help Genie for 24/7 call answering, appointment booking, and emergency triage',
      timeline: '1 week',
      results: [
        {
          metric: 'Calls Captured',
          value: '100%',
          improvement: 'Up from 70%'
        },
        {
          metric: 'Booked Jobs',
          value: '+45%',
          improvement: 'Monthly increase'
        },
        {
          metric: 'Emergency Revenue',
          value: '+60%',
          improvement: 'After hours calls captured'
        },
        {
          metric: 'Owner Time Saved',
          value: '15 hrs/week',
          improvement: 'Focus on jobs, not phones'
        }
      ],
      quote: {
        text: "I used to stress about missing calls while I was elbow-deep in a repair. Now Help Genie handles everything professionally and I come out of a job to see appointments already booked. It's like having a full-time receptionist for a fraction of the cost.",
        author: 'Mike Rodriguez',
        title: 'Owner'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Trades Voice Genie Playbook',
      description: 'Complete guide to implementing voice technology for trade businesses',
      url: '/resources/trades-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Growing Your Trade Business with Voice Technology',
      description: 'Learn how successful contractors capture more leads and book more jobs',
      url: '/webinars/trades-growth'
    },
    {
      type: 'template',
      title: 'Emergency Response Scripts',
      description: 'Pre-built voice scripts for plumbing, HVAC, and electrical emergencies',
      url: '/templates/trades-emergency'
    }
  ],

  faqs: [
    {
      question: 'Can the genie check my real availability before booking?',
      answer: 'Yes, Help Genie integrates with ServiceTitan, Housecall Pro, Jobber, Google Calendar and other scheduling systems to see your real-time availability and book appointments directly into your calendar.'
    },
    {
      question: 'How does it handle emergency calls?',
      answer: 'The genie is trained to identify emergency keywords like "leak," "no heat," "sparking," and immediately prioritize those calls. It gathers critical information and can trigger your emergency notification system.'
    },
    {
      question: 'What if someone calls about a service I don\'t offer?',
      answer: 'The genie knows your service offerings and service area. It politely explains when something is outside your scope and can suggest they find another provider, while still capturing their information in case you want to follow up.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "Your phones ring non-stop\nbut you're on the job",
      subtitle: "See how your Genie handles real trade calls — booking jobs, triaging emergencies, and capturing quotes while your hands are full.",
    },
    chatScenarios: [
      {
        id: 'burst_pipe',
        title: 'Plumbing Emergency',
        messages: [
          { type: 'user' as const, text: "I've got water pouring from under my kitchen sink — it won't stop!", delay: 300 },
          { type: 'genie' as const, text: "I understand — that sounds urgent. First, can you turn off the water valve under the sink? It's usually a knob you turn clockwise.", delay: 1200 },
          { type: 'user' as const, text: "Okay I turned it, but there's already water everywhere", delay: 2500 },
          { type: 'genie' as const, text: "Good — you've stopped the flow. I'm going to get a plumber out to you as soon as possible. What's your name and address?", delay: 3400 },
          { type: 'user' as const, text: "Sarah Mitchell, 4521 Oak Lane", delay: 4600 },
          { type: 'genie' as const, text: "Got it, Sarah. I've flagged this as an emergency — a technician will call you within 15 minutes to confirm arrival time.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Emergency severity assessed', delay: 6500 },
          { id: '2', text: 'Customer guided through shutoff', delay: 6700 },
          { id: '3', text: 'Contact & address captured', delay: 6900 },
          { id: '4', text: 'Flagged P1 — emergency dispatch', delay: 7100 },
          { id: '5', text: 'Technician callback scheduled', delay: 7300 },
        ],
      },
      {
        id: 'ac_broken',
        title: 'HVAC Emergency',
        messages: [
          { type: 'user' as const, text: "My AC just stopped working and it's 98 degrees. I have elderly parents at home.", delay: 300 },
          { type: 'genie' as const, text: "I'm sorry to hear that — with elderly family members, we'll treat this as a priority. Have you checked your thermostat and breaker panel?", delay: 1200 },
          { type: 'user' as const, text: "Yes, breaker is fine and thermostat is set to cool but nothing's happening", delay: 2500 },
          { type: 'genie' as const, text: "Understood. I'm marking this as an urgent call. What's your name, address, and the best number to reach you?", delay: 3400 },
          { type: 'user' as const, text: "Tom Rivera, 892 Maple Drive, 555-867-9012", delay: 4600 },
          { type: 'genie' as const, text: "Thank you, Tom. You're on our emergency list — a technician will reach out within 20 minutes. In the meantime, close blinds and use fans to keep everyone cool.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Vulnerability flagged (elderly)', delay: 6500 },
          { id: '2', text: 'Basic troubleshooting attempted', delay: 6700 },
          { id: '3', text: 'Emergency priority assigned', delay: 6900 },
          { id: '4', text: 'Full contact details captured', delay: 7100 },
          { id: '5', text: 'Safety advice provided', delay: 7300 },
        ],
      },
      {
        id: 'furnace_tuneup',
        title: 'Service Scheduling',
        messages: [
          { type: 'user' as const, text: "Winter's coming — I want to get my furnace checked before it gets cold. Can I schedule a tune-up?", delay: 300 },
          { type: 'genie' as const, text: "Great idea — preventive maintenance saves you money and headaches. We have openings next Tuesday and Thursday morning. Which works better?", delay: 1200 },
          { type: 'user' as const, text: "Thursday morning would be perfect", delay: 2500 },
          { type: 'genie' as const, text: "Thursday it is. Do you know the make and model of your furnace? It helps our tech come prepared.", delay: 3400 },
          { type: 'user' as const, text: "It's a Carrier, maybe 8 years old? Not sure of the model", delay: 4600 },
          { type: 'genie' as const, text: "No problem — Carrier, approximately 8 years old. You're booked for Thursday between 8 and 10 AM. You'll get a confirmation text shortly.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Service type identified', delay: 6500 },
          { id: '2', text: 'Appointment booked', delay: 6700 },
          { id: '3', text: 'Equipment details captured', delay: 6900 },
          { id: '4', text: 'Confirmation sent', delay: 7100 },
          { id: '5', text: 'Seasonal upsell opportunity logged', delay: 7300 },
        ],
      },
      {
        id: 'electrical_quote',
        title: 'Quote Request',
        messages: [
          { type: 'user' as const, text: "I'm converting my garage into a workshop and need a sub-panel installed. Can I get a quote?", delay: 300 },
          { type: 'genie' as const, text: "Absolutely — a sub-panel for a workshop is a common job. How many amps are you looking for, and do you plan to run any heavy equipment like a welder or compressor?", delay: 1200 },
          { type: 'user' as const, text: "I'll have a welder, table saw, and air compressor. Not sure on amps.", delay: 2500 },
          { type: 'genie' as const, text: "With that equipment, you'll likely need a 60 or 100 amp sub-panel. I'll have our electrician review and give you an exact quote. What's your name and contact info?", delay: 3400 },
          { type: 'user' as const, text: "Dave Chen, 555-234-7890, I'm at 1120 Industrial Blvd", delay: 4600 },
          { type: 'genie' as const, text: "Great, Dave. I've captured your project details — our electrician will call you within 24 hours with a quote. Is there anything else you need?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Project scope documented', delay: 6500 },
          { id: '2', text: 'Equipment load assessed', delay: 6700 },
          { id: '3', text: 'Contact info captured', delay: 6900 },
          { id: '4', text: 'Quote follow-up scheduled', delay: 7100 },
          { id: '5', text: 'Qualified lead created', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_leak',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "Hi, I know it's late — I just noticed a slow drip from my water heater. Should I be worried?", delay: 300 },
          { type: 'genie' as const, text: "It's good you called. A dripping water heater can be a sign of pressure buildup or a failing tank. Is the drip coming from the bottom or from a valve on the side?", delay: 1200 },
          { type: 'user' as const, text: "Looks like the bottom", delay: 2500 },
          { type: 'genie' as const, text: "That could mean the tank is corroding. It's not an immediate emergency, but you'll want it looked at soon. I can have someone call you first thing tomorrow morning to schedule a visit.", delay: 3400 },
          { type: 'user' as const, text: "That'd be great. It's Lisa Park, 555-345-6789", delay: 4600 },
          { type: 'genie' as const, text: "Got it, Lisa. I'll have a technician call you by 8 AM. In the meantime, put a bucket underneath and keep an eye on it. If it gets worse, call us right back.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Issue assessed remotely', delay: 6500 },
          { id: '2', text: 'Severity triaged correctly', delay: 6700 },
          { id: '3', text: 'After hours lead captured', delay: 6900 },
          { id: '4', text: 'Morning callback queued', delay: 7100 },
          { id: '5', text: 'Safety guidance provided', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Stop Losing Jobs to Voicemail",
      subtitle: "When you're on a ladder, under a sink, or inside a crawlspace — your Genie answers the phone so you don't lose the next job.",
    },
    useCaseCards: [
      {
        id: 'emergency-dispatch',
        title: 'Emergency Call Triage',
        description: "Burst pipes, no heat, electrical sparking — your Genie assesses urgency and dispatches the right response instantly.",
        bullets: [
          'Identifies emergency keywords automatically',
          'Guides callers through safety steps',
          'Routes to on call technician immediately',
          'Captures address and access details',
        ],
        image: '/solutions-pages/trades/usecase-emergency-triage.webp',
      },
      {
        id: 'job-booking',
        title: 'Job Booking & Scheduling',
        description: "Customers book service calls while you're elbow-deep in a repair. Your Genie checks availability and confirms appointments.",
        bullets: [
          'Syncs with ServiceTitan, Housecall Pro, or Jobber',
          'Books into real open time slots',
          'Sends confirmation texts automatically',
          'Reduces no-shows with reminders',
        ],
        image: '/solutions-pages/trades/usecase-job-booking.webp',
      },
      {
        id: 'quote-capture',
        title: 'Quote Request Capture',
        description: "Capture every project lead with full scope details — so you can quote accurately without playing phone tag.",
        bullets: [
          'Gathers project type, size, and timeline',
          'Verifies service area before you waste a trip',
          'Asks about permits and special requirements',
          'Delivers qualified leads ready to quote',
        ],
        image: '/solutions-pages/trades/usecase-quote-capture.webp',
      },
      {
        id: 'after hours-capture',
        title: 'After Hours Lead Capture',
        description: "Homeowners search for help at night and on weekends. Your Genie captures those leads instead of sending them to a competitor.",
        bullets: [
          'Answers every call — evenings, weekends, holidays',
          'Captures full contact and job details',
          'Sends you instant SMS notifications',
          'Books next-day appointments automatically',
        ],
        image: '/solutions-pages/trades/usecase-after hours.webp',
      },
    ],
    finalCTA: {
      title: "Your Phones Ring. Your Genie Answers.",
      subtitle: "While you're on the job site, your Genie is booking the next one — capturing every lead, triaging every emergency, and making sure no customer hears voicemail.",
      pillars: [
        {
          title: 'Always\nAnswering',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Every call answered professionally — even when you\'re on a roof or under a house.',
        },
        {
          title: 'Always\nDispatching',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Emergencies triaged and routed to on call techs in seconds, not hours.',
        },
        {
          title: 'Always\nBooking',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Jobs booked into your real calendar — no double-booking, no phone tag.',
        },
      ],
      backgroundImage: '/solutions-pages/trades/cta-background.webp',
    },
  }
};
